// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useRole } from "@/contexts/RoleContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Plus, Download, BedDouble } from "lucide-react";
import { toast } from "sonner";

/**
 * HotelPartnerDashboard — global view (tabs across all assigned events).
 * Per-event drilldown is the same component, scoped via :eventId.
 *
 * TODO (future): public event page should render Stay & Play section using
 * stay_to_play_mode + hotel_event_assignments + hotel_properties.
 */
export default function HotelPartnerDashboard() {
  const { eventId } = useParams<{ eventId?: string }>();
  const { user } = useAuth();
  const { roles } = useRole();
  const navigate = useNavigate();
  const isAdmin = roles.includes("rlevents_super_admin") || roles.includes("admin");

  const [assignments, setAssignments] = useState<any[]>([]);
  const [properties, setProperties] = useState<any[]>([]);
  const [bookings, setBookings] = useState<any[]>([]);
  const [rfps, setRfps] = useState<any[]>([]);
  const [eventInfo, setEventInfo] = useState<any>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      // Assignments scoped to this partner (or all, for admins)
      let q = (supabase as any)
        .from("hotel_event_assignments")
        .select("*, events:event_id (id, name, start_date, end_date)");
      if (eventId) q = q.eq("event_id", eventId);
      else if (!isAdmin) q = q.eq("hotel_partner_user_id", user.id);
      const { data: a } = await q;
      setAssignments(a || []);

      // Build event-id filter
      const eventIds = eventId ? [eventId] : (a || []).map((row: any) => row.event_id);
      if (eventIds.length === 0) {
        setProperties([]);
        setBookings([]);
        setRfps([]);
        return;
      }

      const [{ data: p }, { data: b }, { data: r }] = await Promise.all([
        (supabase as any).from("hotel_properties").select("*").in("event_id", eventIds),
        (supabase as any).from("hotel_bookings").select("*").in("event_id", eventIds),
        (supabase as any).from("hotel_rfp_requests").select("*").in("event_id", eventIds),
      ]);
      setProperties(p || []);
      setBookings(b || []);
      setRfps(r || []);

      if (eventId) {
        const { data: ev } = await (supabase as any)
          .from("events")
          .select("id, name, start_date, end_date")
          .eq("id", eventId)
          .maybeSingle();
        setEventInfo(ev);
      }
    })();
  }, [user?.id, eventId, isAdmin]);

  const stats = useMemo(() => {
    const grossRevenue = bookings.reduce((s, b) => s + (Number(b.gross_revenue) || 0), 0);
    return {
      events: assignments.length,
      properties: properties.length,
      activeBookings: bookings.filter((b) => b.status === "confirmed" || b.status === "active").length,
      grossRevenue,
    };
  }, [assignments, properties, bookings]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <BedDouble className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">
            {eventInfo ? `Hotels — ${eventInfo.name}` : "Hotels"}
          </h1>
          {eventInfo && (
            <p className="text-sm text-muted-foreground">
              {eventInfo.start_date} → {eventInfo.end_date}
            </p>
          )}
        </div>
      </div>

      {!eventId && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="My Events" value={stats.events} />
          <StatCard label="Properties" value={stats.properties} />
          <StatCard label="Active Bookings" value={stats.activeBookings} />
          <StatCard label="Gross Revenue" value={`$${stats.grossRevenue.toLocaleString()}`} />
        </div>
      )}

      <Tabs defaultValue={eventId ? "properties" : "events"}>
        <TabsList>
          {!eventId && <TabsTrigger value="events">My Events</TabsTrigger>}
          <TabsTrigger value="properties">Properties</TabsTrigger>
          <TabsTrigger value="bookings">Bookings</TabsTrigger>
          <TabsTrigger value="rfps">RFPs</TabsTrigger>
        </TabsList>

        {!eventId && (
          <TabsContent value="events">
            <DataTable
              empty="No events assigned yet."
              columns={["Event", "Dates", "Properties", "Bookings", "Revenue", ""]}
              rows={assignments.map((a) => {
                const eid = a.event_id;
                const props = properties.filter((p) => p.event_id === eid).length;
                const bks = bookings.filter((b) => b.event_id === eid);
                const rev = bks.reduce((s, b) => s + (Number(b.gross_revenue) || 0), 0);
                return [
                  a.events?.name || eid,
                  `${a.events?.start_date || ""} → ${a.events?.end_date || ""}`,
                  props,
                  bks.length,
                  `$${rev.toLocaleString()}`,
                  <Button
                    key="open"
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/admin-dashboard/hotels/${eid}`)}
                  >
                    Open
                  </Button>,
                ];
              })}
            />
          </TabsContent>
        )}

        <TabsContent value="properties">
          <div className="flex justify-end mb-3">
            <Button size="sm" onClick={() => toast.info("TODO: Add property form")}>
              <Plus className="w-4 h-4 mr-1" /> Add Property
            </Button>
          </div>
          <DataTable
            empty="No properties yet."
            columns={["Hotel", "City", "Rate", "Rooms", "RFP Status"]}
            rows={properties.map((p) => [
              p.hotel_name,
              [p.city, p.state].filter(Boolean).join(", ") || "—",
              p.group_rate_per_night ? `$${p.group_rate_per_night}` : "—",
              p.rooms_contracted ?? "—",
              p.rfp_status || "—",
            ])}
          />
        </TabsContent>

        <TabsContent value="bookings">
          <div className="flex justify-end gap-2 mb-3">
            <Button size="sm" variant="outline" onClick={() => toast.info("TODO: CSV export")}>
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Button size="sm" onClick={() => toast.info("TODO: Manual booking entry form")}>
              <Plus className="w-4 h-4 mr-1" /> Add Booking
            </Button>
          </div>
          <DataTable
            empty="No bookings yet."
            columns={["Team", "Hotel", "Check-in", "Check-out", "Rooms", "Revenue", "Status"]}
            rows={bookings.map((b) => [
              b.team_name || "—",
              b.hotel_name || "—",
              b.check_in_date || "—",
              b.check_out_date || "—",
              b.number_of_rooms ?? "—",
              b.gross_revenue ? `$${Number(b.gross_revenue).toLocaleString()}` : "—",
              b.status || "—",
            ])}
          />
        </TabsContent>

        <TabsContent value="rfps">
          <div className="flex justify-end mb-3">
            <Button size="sm" onClick={() => toast.info("TODO: New RFP form")}>
              <Plus className="w-4 h-4 mr-1" /> New RFP
            </Button>
          </div>
          <DataTable
            empty="No RFPs sent yet."
            columns={["Hotel", "Sent", "Status", "Offered Rate"]}
            rows={rfps.map((r) => [
              r.hotel_name,
              r.sent_at ? new Date(r.sent_at).toLocaleDateString() : "—",
              <span
                key="s"
                className="px-2 py-0.5 rounded-full text-xs bg-muted text-foreground"
              >
                {r.status}
              </span>,
              r.offered_rate ? `$${r.offered_rate}` : "—",
            ])}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: any }) {
  return (
    <div className="p-4 rounded-lg border border-border bg-card">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function DataTable({
  columns,
  rows,
  empty,
}: {
  columns: string[];
  rows: any[][];
  empty: string;
}) {
  if (rows.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground border border-dashed rounded-lg">
        {empty}
      </div>
    );
  }
  return (
    <div className="overflow-auto border border-border rounded-lg">
      <table className="w-full text-sm">
        <thead className="bg-muted">
          <tr>
            {columns.map((c) => (
              <th key={c} className="text-left px-3 py-2 font-semibold">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border">
              {r.map((cell, j) => (
                <td key={j} className="px-3 py-2">
                  {cell as any}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
